import { useAuth } from '@/hooks/useAuth';
import { exitGodModeSafely } from '@/services/agency.service';
import { toast } from '@/utils/toast';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import { devLog } from '@/utils/logger';
import { Icon } from '@/components/ui/Icon';
import { getContrastTextColor } from '@/utils/colors';

/**
 * God Mode Indicator
 *
 * Displays when an agency user is impersonating a client workspace.
 * - Desktop: inline bar with team name + exit button
 * - Mobile: compact icon + badge, tap to expand dropdown
 */

export const GodModeIndicator = () => {
  const { user, refreshUser } = useAuth();
  const [isExiting, setIsExiting] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation('common');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileOpen]);

  // Only show if user has active God Mode session
  if (!user?.godModeSession) {
    return null;
  }

  const { targetTeamName, targetTeamIcon, targetTeamColor } = user.godModeSession;

  const indicatorBg = targetTeamColor ? targetTeamColor : undefined;
  const contrastClass = targetTeamColor ? getContrastTextColor(targetTeamColor) : 'text-gray-900';
  const indicatorStyle = indicatorBg ? { backgroundColor: indicatorBg } : undefined;

  /**
   * Exit God Mode
   */
  const handleExitGodMode = async () => {
    setIsExiting(true);

    try {
      await exitGodModeSafely();
    } catch (error) {
      // Genuine failure (network/5xx). Do not trap the user: still clear
      // local state and land on the portfolio below.
      devLog.error('[GodModeIndicator] Failed to exit God Mode:', error);
      toast.error(t('error_exit_god_mode'));
    }
    try {
      await refreshUser();
    } catch (error) {
      devLog.error('[GodModeIndicator] refreshUser failed after exit:', error);
    }
    setIsExiting(false);
    window.location.href = '/agency';
  };

  const leadIcon = targetTeamIcon ? (
    <Icon name={targetTeamIcon} size="sm" className={`flex-shrink-0 ${contrastClass}`} />
  ) : (
    <svg
      className="w-4 h-4 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );

  return (
    <>
      {/* Mobile: compact icon + team name, tap to expand */}
      <div className="lg:hidden relative" ref={dropdownRef}>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`flex items-center gap-1.5 px-2 py-1.5 relative rounded-lg shadow-md text-xs font-medium max-w-[140px] max-[379px]:max-w-[100px] max-[359px]:max-w-[80px] before:absolute before:inset-x-0 before:-inset-y-[10px] before:content-[''] ${!targetTeamColor ? 'bg-peach text-gray-900' : contrastClass}`}
          style={indicatorStyle}
          aria-label={`${t('god_mode_viewing')} ${targetTeamName}`}
          title={targetTeamName}
        >
          {leadIcon}
          <span className="font-bold truncate">{targetTeamName}</span>
        </button>

        {/* Dropdown */}
        {mobileOpen && (
          <div className="fixed left-4 right-4 top-[4.5rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
            <div className="flex items-center gap-2 mb-3">
              {leadIcon}
              <span className="text-sm font-medium text-gray-900">
                {t('god_mode_viewing')} <strong>{targetTeamName}</strong>
              </span>
            </div>
            <button
              onClick={handleExitGodMode}
              disabled={isExiting}
              className="w-full min-h-[44px] px-3 py-2 border border-bs-black text-bs-black rounded-md text-sm font-semibold hover:bg-bs-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('aria_exit_god_mode')}
            >
              {isExiting ? '...' : t('god_mode_exit_to_cc')}
            </button>
          </div>
        )}
      </div>

      {/* Desktop: inline bar */}
      <div
        className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg shadow-md max-w-full ${!targetTeamColor ? 'bg-peach text-gray-900' : contrastClass}`}
        style={indicatorStyle}
        role="alert"
        aria-live="polite"
      >
        {leadIcon}
        <Tooltip content={targetTeamName}>
          <span className="text-xs font-medium truncate max-w-[200px]">
            {t('god_mode_viewing')} <strong className="font-bold">{targetTeamName}</strong>
          </span>
        </Tooltip>
        <Tooltip content={t('aria_exit_god_mode')}>
          <button
            onClick={handleExitGodMode}
            disabled={isExiting}
            className={`flex-shrink-0 px-2 py-1 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              targetTeamColor
                ? `${contrastClass} border-current hover:bg-white/15`
                : 'border-bs-black text-bs-black hover:bg-bs-black hover:text-white'
            }`}
            aria-label={t('aria_exit_god_mode')}
          >
            <span className="block max-w-[160px] truncate">
              {isExiting ? '...' : t('god_mode_exit_to_cc')}
            </span>
          </button>
        </Tooltip>
      </div>
    </>
  );
};
